import React, { useState, useEffect, useRef } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Switch } from '@/components/ui/switch';
import { useToast } from '@/hooks/use-toast';
import { useTheme } from '@/context/ThemeContext';
import { 
  Palette, 
  BellRing, 
  Globe, 
  ShieldCheck, 
  Upload, 
  Mail,
  Search,
  Lock,
  CheckCircle
} from 'lucide-react';

export default function SiteSettings() {
  const { toast } = useToast();
  const { 
    isDarkMode, 
    setIsDarkMode, 
    lightLogo, 
    setLightLogo, 
    darkLogo, 
    setDarkLogo, 
    favicon, 
    setFavicon 
  } = useTheme();
  
  const [activeTab, setActiveTab] = useState('theme');
  const [saving, setSaving] = useState(false);
  
  // Theme color state
  const [primaryColor, setPrimaryColor] = useState('#6366f1');
  const [primaryColorText, setPrimaryColorText] = useState('#6366f1');
  const [secondaryColor, setSecondaryColor] = useState('#8b5cf6');
  const [secondaryColorText, setSecondaryColorText] = useState('#8b5cf6');
  const [accentColor, setAccentColor] = useState('#06b6d4');
  const [accentColorText, setAccentColorText] = useState('#06b6d4');
  
  // Logo image state
  const [lightLogoSrc, setLightLogoSrc] = useState(lightLogo || './assets/aivia-logo-blue-transparent-crop.png');
  const [darkLogoSrc, setDarkLogoSrc] = useState(darkLogo || './assets/aivia-logo-blue-transparent-crop.png');
  const [faviconSrc, setFaviconSrc] = useState(favicon || './assets/aivia-favicon.png');
  
  // File upload refs
  const lightLogoRef = useRef<HTMLInputElement>(null);
  const darkLogoRef = useRef<HTMLInputElement>(null);
  const faviconRef = useRef<HTMLInputElement>(null);
  
  // Add state for Smart Workspace toggle
  const [smartWorkspaceEnabled, setSmartWorkspaceEnabled] = useState(false);
  const [savingFeature, setSavingFeature] = useState(false);
  const isAdmin = true; // TODO: Replace with real admin check
  
  // Load site settings on mount
  useEffect(() => {
    fetch('/api/site-settings')
      .then(res => res.json())
      .then(data => {
        if (typeof data.smartWorkspaceEnabled === 'boolean') {
          setSmartWorkspaceEnabled(data.smartWorkspaceEnabled);
        }
      });
  }, []);
  
  // AR-393 — the dark-mode effect that used to live here is gone. ThemeProvider
  // is the only writer of the appearance now, so this page, the Settings module
  // and the user menu cannot disagree about it.

  // Handle color input changes
  const handlePrimaryColorChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setPrimaryColor(value);
    setPrimaryColorText(value);
  };
  
  const handleSecondaryColorChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSecondaryColor(value);
    setSecondaryColorText(value);
  };
  
  const handleAccentColorChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setAccentColor(value);
    setAccentColorText(value);
  };
  
  // Handle text input changes (validate hex color)
  const handlePrimaryTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setPrimaryColorText(value);
    if (/^#([0-9A-F]{3}){1,2}$/i.test(value)) {
      setPrimaryColor(value);
    }
  };
  
  const handleSecondaryTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSecondaryColorText(value);
    if (/^#([0-9A-F]{3}){1,2}$/i.test(value)) {
      setSecondaryColor(value);
    }
  };
  
  const handleAccentTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setAccentColorText(value);
    if (/^#([0-9A-F]{3}){1,2}$/i.test(value)) {
      setAccentColor(value);
    }
  };
  
  // Handle file uploads
  const handleLogoUpload = (type: 'light' | 'dark' | 'favicon') => {
    let fileInput: HTMLInputElement | null = null;
    
    switch (type) {
      case 'light':
        fileInput = lightLogoRef.current;
        break;
      case 'dark':
        fileInput = darkLogoRef.current;
        break;
      case 'favicon':
        fileInput = faviconRef.current;
        break;
    }
    
    if (fileInput) {
      fileInput.click();
    }
  };
  
  // Handle file change events
  const handleFileChange = async (
    e: React.ChangeEvent<HTMLInputElement>, 
    type: 'light' | 'dark' | 'favicon'
  ) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    try {
      // Create form data
      const formData = new FormData();
      formData.append('image', file);
      // Map the type to what the server expects
      const serverType = type === 'light' ? 'lightLogo' : type === 'dark' ? 'darkLogo' : 'favicon';
      formData.append('type', serverType);
      
      // Upload the file
      const response = await fetch('/api/site-settings/upload-image', {
        method: 'POST',
        body: formData,
      });
      
      if (!response.ok) {
        throw new Error('Failed to upload image');
      }
      
      const result = await response.json();
      
      if (!result.success) {
        throw new Error(result.error || 'Failed to upload image');
      }
      
      // Update the image source with the new path
      const newPath = result.data.path;
      switch (type) {
        case 'light':
          setLightLogoSrc(newPath);
          setLightLogo(newPath); // Update context
          break;
        case 'dark':
          setDarkLogoSrc(newPath);
          setDarkLogo(newPath); // Update context
          break;
        case 'favicon':
          setFaviconSrc(newPath);
          setFavicon(newPath); // Update context
          break;
      }
      
      toast({
        title: "Image updated",
        description: `The ${type} mode image has been updated.`,
      });
    } catch (error) {
      console.error('Error uploading image:', error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : 'Failed to upload image',
        variant: "destructive",
      });
    }
  };
  
  // Handle theme settings save
  const saveThemeSettings = async () => {
    setSaving(true);
    
    try {
      // Create a theme settings object
      const themeSettings = {
        primary: primaryColor.replace('#', ''),
        variant: 'professional',
        appearance: isDarkMode ? 'dark' : 'light',
        radius: 0.5,
        logos: {
          light: lightLogoSrc,
          dark: darkLogoSrc,
          favicon: faviconSrc
        }
      };
      
      // Update CSS variables for immediate effect
      document.documentElement.style.setProperty('--primary', primaryColor);
      document.documentElement.style.setProperty('--secondary', secondaryColor);
      document.documentElement.style.setProperty('--accent', accentColor);
      
      // Make sure ThemeContext is updated with all the latest values
      setLightLogo(lightLogoSrc);
      setDarkLogo(darkLogoSrc);
      setFavicon(faviconSrc);
      
      toast({
        title: "Theme settings saved",
        description: "Your theme settings have been successfully updated."
      });
    } catch (error) {
      console.error('Error saving theme settings:', error);
      toast({
        title: "Error",
        description: "Failed to save theme settings",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleSmartWorkspaceToggle = async (checked: boolean) => {
    setSavingFeature(true);
    try {
      const res = await fetch('/api/site-settings', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ smartWorkspaceEnabled: checked })
      });
      const data = await res.json();
      if (res.ok && typeof data.smartWorkspaceEnabled === 'boolean') {
        setSmartWorkspaceEnabled(data.smartWorkspaceEnabled);
        toast({ title: 'Smart Workspace updated', description: `Smart Workspace is now ${data.smartWorkspaceEnabled ? 'enabled' : 'disabled'}.` });
      } else {
        throw new Error(data.error || 'Failed to update setting');
      }
    } catch (error) {
      toast({ title: 'Error', description: error instanceof Error ? error.message : 'Failed to update setting', variant: 'destructive' });
    } finally {
      setSavingFeature(false);
    }
  };

  return (
    <div className="container p-6 mx-auto">
      <div className="flex flex-col space-y-4">
        <div>
          <h1 className="text-3xl font-bold">Site Settings</h1>
          <p className="text-muted-foreground">Manage your website configuration and preferences</p>
        </div>

        <Tabs defaultValue="theme" value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid grid-cols-5 w-full max-w-3xl">
            <TabsTrigger value="theme" className="flex items-center gap-2">
              <Palette className="h-4 w-4" />
              <span>Theme</span>
            </TabsTrigger>
            <TabsTrigger value="notifications" className="flex items-center gap-2">
              <BellRing className="h-4 w-4" />
              <span>Notifications & Emails</span>
            </TabsTrigger>
            <TabsTrigger value="seo" className="flex items-center gap-2">
              <Globe className="h-4 w-4" />
              <span>SEO & Metadata</span>
            </TabsTrigger>
            <TabsTrigger value="security" className="flex items-center gap-2">
              <ShieldCheck className="h-4 w-4" />
              <span>Security</span>
            </TabsTrigger>
            <TabsTrigger value="features" className="flex items-center gap-2">
              <Search className="h-4 w-4" />
              <span>Feature Toggles</span>
            </TabsTrigger>
          </TabsList>

          {/* Theme Settings */}
          <TabsContent value="theme" className="space-y-4 mt-6">
            <Card>
              <CardHeader>
                <CardTitle>Branding & Visual Identity</CardTitle>
                <CardDescription>
                  Customize your site's appearance, logo, and color scheme
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  {/* Logo Section */}
                  <div className="space-y-4">
                    <h3 className="text-lg font-medium">Logo & Favicon</h3>
                    <Separator />
                    
                    <div className="space-y-2">
                      <Label htmlFor="logo">Logo (Light Mode)</Label>
                      <div className="flex items-end gap-4">
                        <div className="w-32 h-32 flex items-center justify-center border border-dashed rounded-md bg-gray-50 dark:bg-gray-900">
                          <img 
                            src={lightLogoSrc} 
                            alt="AIVIA Logo" 
                            className="max-w-full max-h-full p-2"
                          />
                        </div>
                        <Button 
                          size="sm" 
                          className="flex items-center gap-2"
                          onClick={() => handleLogoUpload('light')}
                        >
                          <Upload className="h-4 w-4" />
                          Change
                        </Button>
                        <input 
                          ref={lightLogoRef}
                          type="file" 
                          accept="image/png, image/jpeg, image/svg+xml" 
                          className="hidden"
                          onChange={(e) => handleFileChange(e, 'light')}
                        />
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="dark-logo">Logo (Dark Mode)</Label>
                      <div className="flex items-end gap-4">
                        <div className="w-32 h-32 flex items-center justify-center border border-dashed rounded-md bg-gray-800">
                          <img 
                            src={darkLogoSrc} 
                            alt="AIVIA Logo (Dark)" 
                            className="max-w-full max-h-full p-2"
                          />
                        </div>
                        <Button 
                          size="sm" 
                          className="flex items-center gap-2"
                          onClick={() => handleLogoUpload('dark')}
                        >
                          <Upload className="h-4 w-4" />
                          Change
                        </Button>
                        <input 
                          ref={darkLogoRef}
                          type="file" 
                          accept="image/png, image/jpeg, image/svg+xml" 
                          className="hidden"
                          onChange={(e) => handleFileChange(e, 'dark')}
                        />
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="favicon">Favicon</Label>
                      <div className="flex items-end gap-4">
                        <div className="w-16 h-16 flex items-center justify-center border border-dashed rounded-md bg-gray-50 dark:bg-gray-900">
                          <img 
                            src={faviconSrc} 
                            alt="Favicon" 
                            className="max-w-full max-h-full p-1"
                          />
                        </div>
                        <Button 
                          size="sm" 
                          className="flex items-center gap-2"
                          onClick={() => handleLogoUpload('favicon')}
                        >
                          <Upload className="h-4 w-4" />
                          Change
                        </Button>
                        <input 
                          ref={faviconRef}
                          type="file" 
                          accept="image/png, image/ico, image/x-icon" 
                          className="hidden"
                          onChange={(e) => handleFileChange(e, 'favicon')}
                        />
                      </div>
                      <p className="text-xs text-muted-foreground">
                        Recommended: 32x32px PNG/ICO file
                      </p>
                    </div>
                  </div>
                  
                  {/* Color Scheme */}
                  <div className="space-y-4">
                    <h3 className="text-lg font-medium">Color Palette</h3>
                    <Separator />
                    
                    <div className="space-y-2">
                      <Label htmlFor="primary-color">Primary Color</Label>
                      <div className="flex items-center gap-2">
                        <Input 
                          id="primary-color" 
                          type="color" 
                          value={primaryColor}
                          onChange={handlePrimaryColorChange}
                          className="w-16 h-10"
                        />
                        <Input 
                          type="text" 
                          value={primaryColorText}
                          onChange={handlePrimaryTextChange}
                          className="w-32"
                        />
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="secondary-color">Secondary Color</Label>
                      <div className="flex items-center gap-2">
                        <Input 
                          id="secondary-color" 
                          type="color" 
                          value={secondaryColor}
                          onChange={handleSecondaryColorChange}
                          className="w-16 h-10"
                        />
                        <Input 
                          type="text" 
                          value={secondaryColorText}
                          onChange={handleSecondaryTextChange}
                          className="w-32"
                        />
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="accent-color">Accent Color</Label>
                      <div className="flex items-center gap-2">
                        <Input 
                          id="accent-color" 
                          type="color" 
                          value={accentColor}
                          onChange={handleAccentColorChange}
                          className="w-16 h-10"
                        />
                        <Input 
                          type="text" 
                          value={accentColorText}
                          onChange={handleAccentTextChange}
                          className="w-32"
                        />
                      </div>
                    </div>
                    
                    <div className="flex items-center justify-between pt-4">
                      <div className="space-y-0.5">
                        <Label htmlFor="dark-mode">Dark Mode Default</Label>
                        <p className="text-sm text-muted-foreground">
                          Set dark mode as the default theme
                        </p>
                      </div>
                      <Switch 
                        id="dark-mode" 
                        checked={isDarkMode}
                        onCheckedChange={setIsDarkMode}
                      />
                    </div>
                  </div>
                </div>
              </CardContent>
              <CardFooter className="flex justify-end">
                <Button 
                  onClick={saveThemeSettings} 
                  disabled={saving}
                  className="flex items-center gap-2"
                >
                  {saving ? (
                    <>
                      <span className="h-4 w-4 animate-spin rounded-full border-2 border-t-transparent"></span>
                      Saving...
                    </>
                  ) : (
                    <>
                      <CheckCircle className="h-4 w-4" />
                      Save Changes
                    </>
                  )}
                </Button>
              </CardFooter>
            </Card>
          </TabsContent>

          {/* Notifications & Emails Settings */}
          <TabsContent value="notifications" className="space-y-4 mt-6">
            <Card>
              <CardHeader>
                <CardTitle>Notifications & Email Configuration</CardTitle>
                <CardDescription>
                  Configure email settings and notification preferences
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-4">
                  <h3 className="text-lg font-medium">Email Settings</h3>
                  <Separator />
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="smtp-host">SMTP Host</Label>
                      <Input id="smtp-host" placeholder="mail.example.com" />
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="smtp-port">SMTP Port</Label>
                      <Input id="smtp-port" placeholder="587" />
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="smtp-user">SMTP Username</Label>
                      <Input id="smtp-user" placeholder="notifications@example.com" />
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="smtp-pass">SMTP Password</Label>
                      <Input id="smtp-pass" type="password" placeholder="••••••••••" />
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="from-email">From Email</Label>
                      <Input id="from-email" placeholder="no-reply@example.com" />
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="from-name">From Name</Label>
                      <Input id="from-name" placeholder="AIVIA Notifications" />
                    </div>
                  </div>
                </div>
                
                <div className="space-y-4 pt-4">
                  <h3 className="text-lg font-medium">Notification Templates</h3>
                  <Separator />
                  
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <div className="space-y-0.5">
                        <div className="font-medium">Welcome Email</div>
                        <div className="text-sm text-muted-foreground">
                          Sent to new users after registration
                        </div>
                      </div>
                      <Button variant="outline" size="sm" className="gap-1">
                        <Mail className="h-4 w-4" />
                        Edit Template
                      </Button>
                    </div>
                    
                    <Separator />
                    
                    <div className="flex items-center justify-between">
                      <div className="space-y-0.5">
                        <div className="font-medium">Password Reset</div>
                        <div className="text-sm text-muted-foreground">
                          Sent when a user requests a password reset
                        </div>
                      </div>
                      <Button variant="outline" size="sm" className="gap-1">
                        <Mail className="h-4 w-4" />
                        Edit Template
                      </Button>
                    </div>
                    
                    <Separator />
                    
                    <div className="flex items-center justify-between">
                      <div className="space-y-0.5">
                        <div className="font-medium">Trip Confirmation</div>
                        <div className="text-sm text-muted-foreground">
                          Sent when a trip is confirmed with client
                        </div>
                      </div>
                      <Button variant="outline" size="sm" className="gap-1">
                        <Mail className="h-4 w-4" />
                        Edit Template
                      </Button>
                    </div>
                  </div>
                </div>
              </CardContent>
              <CardFooter className="flex justify-end">
                <Button>Save Changes</Button>
              </CardFooter>
            </Card>
          </TabsContent>

          {/* SEO & Metadata Settings */}
          <TabsContent value="seo" className="space-y-4 mt-6">
            <Card>
              <CardHeader>
                <CardTitle>SEO & Metadata Settings</CardTitle>
                <CardDescription>
                  Configure search engine optimization and metadata for your site
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-4">
                  <h3 className="text-lg font-medium">Global SEO Settings</h3>
                  <Separator />
                  
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="site-title">Site Title</Label>
                      <Input id="site-title" placeholder="AIVIA Travel Companion" />
                      <p className="text-xs text-muted-foreground">
                        Used in browser tabs and search results
                      </p>
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="meta-description">Meta Description</Label>
                      <Input 
                        id="meta-description" 
                        placeholder="AIVIA - Your AI-powered travel planning assistant"
                      />
                      <p className="text-xs text-muted-foreground">
                        Appears in search result snippets (Recommended: 150-160 characters)
                      </p>
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="meta-keywords">Meta Keywords</Label>
                      <Input 
                        id="meta-keywords" 
                        placeholder="travel, AI assistant, trip planning, vacation"
                      />
                      <p className="text-xs text-muted-foreground">
                        Comma-separated keywords (less important for modern SEO)
                      </p>
                    </div>
                  </div>
                </div>
                
                <div className="space-y-4 pt-4">
                  <h3 className="text-lg font-medium">Social Media Integration</h3>
                  <Separator />
                  
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="og-title">Open Graph Title</Label>
                      <Input id="og-title" placeholder="Plan Your Next Adventure with AIVIA" />
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="og-description">Open Graph Description</Label>
                      <Input 
                        id="og-description" 
                        placeholder="AI-powered travel planning that creates personalized itineraries"
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="og-image">Open Graph Image</Label>
                      <div className="flex items-end gap-4">
                        <div className="w-40 h-24 flex items-center justify-center border border-dashed rounded-md bg-gray-50 dark:bg-gray-900">
                          <img 
                            src="./assets/aivia-logo-blue-transparent-crop.png" 
                            alt="OG Image" 
                            className="max-w-full max-h-full p-2"
                          />
                        </div>
                        <Button size="sm" className="flex items-center gap-2">
                          <Upload className="h-4 w-4" />
                          Change
                        </Button>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        Recommended size: 1200x630 pixels
                      </p>
                    </div>
                  </div>
                </div>
                
                <div className="space-y-4 pt-4">
                  <h3 className="text-lg font-medium">Indexing Controls</h3>
                  <Separator />
                  
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <div className="space-y-0.5">
                        <Label htmlFor="enable-indexing">Enable Search Indexing</Label>
                        <p className="text-sm text-muted-foreground">
                          Allow search engines to index your site
                        </p>
                      </div>
                      <Switch id="enable-indexing" defaultChecked />
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <div className="space-y-0.5">
                        <Label htmlFor="generate-sitemap">Auto-generate Sitemap</Label>
                        <p className="text-sm text-muted-foreground">
                          Creates and updates sitemap.xml automatically
                        </p>
                      </div>
                      <Switch id="generate-sitemap" defaultChecked />
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <div className="space-y-0.5">
                        <Label htmlFor="analytics">Enable Google Analytics</Label>
                        <p className="text-sm text-muted-foreground">
                          Track visits and user behavior
                        </p>
                      </div>
                      <Switch id="analytics" />
                    </div>
                  </div>
                </div>
              </CardContent>
              <CardFooter className="flex justify-end">
                <Button>Save Changes</Button>
              </CardFooter>
            </Card>
          </TabsContent>

          {/* Security Settings */}
          <TabsContent value="security" className="space-y-4 mt-6">
            <Card>
              <CardHeader>
                <CardTitle>Security Settings</CardTitle>
                <CardDescription>
                  Configure security policies and access controls
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-4">
                  <h3 className="text-lg font-medium">Authentication Settings</h3>
                  <Separator />
                  
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <div className="space-y-0.5">
                        <Label htmlFor="two-factor">Two-Factor Authentication</Label>
                        <p className="text-sm text-muted-foreground">
                          Require 2FA for administrator accounts
                        </p>
                      </div>
                      <Switch id="two-factor" />
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <div className="space-y-0.5">
                        <Label htmlFor="lockout">Account Lockout</Label>
                        <p className="text-sm text-muted-foreground">
                          Lock accounts after 5 failed login attempts
                        </p>
                      </div>
                      <Switch id="lockout" defaultChecked />
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="session-timeout">Session Timeout (minutes)</Label>
                      <Input id="session-timeout" type="number" defaultValue="60" />
                      <p className="text-xs text-muted-foreground">
                        Time before user is automatically logged out
                      </p>
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="password-policy">Password Policy</Label>
                      <div className="flex items-center space-x-4">
                        <select className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50">
                          <option value="standard">Standard (8+ chars, 1 number)</option>
                          <option value="strong">Strong (10+ chars, numbers, symbols)</option>
                          <option value="very-strong">Very Strong (12+ chars, mixed case, numbers, symbols)</option>
                        </select>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="space-y-4 pt-4">
                  <h3 className="text-lg font-medium">Data Protection</h3>
                  <Separator />
                  
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <div className="space-y-0.5">
                        <Label htmlFor="force-ssl">Force HTTPS</Label>
                        <p className="text-sm text-muted-foreground">
                          Redirect all HTTP traffic to HTTPS
                        </p>
                      </div>
                      <Switch id="force-ssl" defaultChecked />
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <div className="space-y-0.5">
                        <Label htmlFor="content-security">Content Security Policy</Label>
                        <p className="text-sm text-muted-foreground">
                          Enable strict CSP headers
                        </p>
                      </div>
                      <Switch id="content-security" />
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <div className="space-y-0.5">
                        <Label htmlFor="data-backup">Automated Backups</Label>
                        <p className="text-sm text-muted-foreground">
                          Schedule daily database backups
                        </p>
                      </div>
                      <Switch id="data-backup" defaultChecked />
                    </div>
                  </div>
                </div>
                
                <div className="space-y-4 pt-4">
                  <h3 className="text-lg font-medium">API & Integration Security</h3>
                  <Separator />
                  
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <div className="space-y-0.5">
                        <Label htmlFor="api-keys">API Key Rotation</Label>
                        <p className="text-sm text-muted-foreground">
                          Automatically rotate API keys every 90 days
                        </p>
                      </div>
                      <Switch id="api-keys" />
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <div className="space-y-0.5">
                        <Label htmlFor="cors-policy">CORS Policy</Label>
                        <p className="text-sm text-muted-foreground">
                          Restrict API access to allowed domains
                        </p>
                      </div>
                      <Switch id="cors-policy" defaultChecked />
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="allowed-origins">Allowed Origins</Label>
                      <Input 
                        id="allowed-origins" 
                        placeholder="https://example.com, https://api.example.com" 
                      />
                      <p className="text-xs text-muted-foreground">
                        Comma-separated list of allowed origins for CORS
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
              <CardFooter className="flex justify-between">
                <Button variant="destructive" className="flex items-center gap-2">
                  <Lock className="h-4 w-4" />
                  Reset to Defaults
                </Button>
                <Button>Save Changes</Button>
              </CardFooter>
            </Card>
          </TabsContent>

          {/* Feature Toggles */}
          <TabsContent value="features" className="space-y-4 mt-6">
            <Card>
              <CardHeader>
                <CardTitle>Feature Toggles</CardTitle>
                <CardDescription>Enable or disable experimental features for your workspace</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label htmlFor="smart-workspace">Smart Workspace</Label>
                    <p className="text-sm text-muted-foreground">Show the Smart Workspace panel on the /threads page</p>
                  </div>
                  <Switch
                    id="smart-workspace"
                    checked={smartWorkspaceEnabled}
                    onCheckedChange={handleSmartWorkspaceToggle}
                    disabled={!isAdmin || savingFeature}
                  />
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}